import React, { useRef } from 'react';
import styled from 'styled-components';
import { OrbitControls } from '@react-three/drei';
import { Quaternion, Vector3, Euler } from 'three';
import { useGlobal } from '../../../logic/main';
import { boardConfig } from '../../../config/boardConfig';
import { OrbitControls as OrbitControlsImpl } from 'three-stdlib/controls/OrbitControls';

interface Props {
    isEnabled?: boolean;
}

const FixedInfo = styled.div`
    position: fixed;
    bottom: 20px;
    right: 20px;
    background-color: rgba(255, 255, 255, 0.75);
    padding: 20px;
    display: flex;
    flex-flow: column;
    gap: 10px;
    height: 90%;
    overflow: scroll;

    > div {
        max-height: 10%;
        overflow: scroll;
    }
`;

export const Debugger: React.FC<Props> = ({ isEnabled }) => {
    const orbitRef = useRef<OrbitControlsImpl>();

    const [{ orbitControlsDisabled, currentCameraConfig }, { setOrbitCameraConfig }] = useGlobal(
        state => ({
            orbitControlsDisabled: state.orbitControlsDisabled,
            currentCameraConfig: state.currentCameraConfig
        }),
        action => ({
            setOrbitCameraConfig: action.setOrbitCameraConfig
        })
    );

    const onOrbitUpdate = () => {
        //console.log("ORBIT CONFIG", orbitRef.current)
        setOrbitCameraConfig({
            position: new Vector3(
                orbitRef.current!.object.position.x,
                orbitRef.current!.object.position.y,
                orbitRef.current!.object.position.z
            ),
            quaternion: new Quaternion(
                orbitRef.current!.object.quaternion.x,
                orbitRef.current!.object.quaternion.y,
                orbitRef.current!.object.quaternion.z,
                orbitRef.current!.object.quaternion.w
            ),
            target: new Vector3(orbitRef.current!.target.x, orbitRef.current!.target.y, orbitRef.current!.target.z),
            rotation: new Euler(
                orbitRef.current!.object.rotation.x,
                orbitRef.current!.object.rotation.y,
                orbitRef.current!.object.rotation.z
            )
        });
    };

    return isEnabled ? (
        <>
            <OrbitControls
                ref={orbitRef as any}
                onChange={onOrbitUpdate}
                enableZoom={true}
                enabled={!orbitControlsDisabled}
                zoomSpeed={0.075}
                position={currentCameraConfig?.position}
                quaternion={currentCameraConfig?.quaternion}
                target={currentCameraConfig?.target}
            />
        </>
    ) : null;
};

export const DebuggerView: React.FC<{ isEnabled?: boolean }> = ({ isEnabled }) => {
    const linesToDraw = useRef<any[]>([]);
    const [
        { orbitCameraConfig, animations, currenAssetTransformation },
        { play, fadeIn, setCameraConfig, setObjectsToTransform, setDrawingLines }
    ] = useGlobal(
        state => ({
            orbitCameraConfig: state.orbitCameraConfig,
            animations: state.animations,
            currenAssetTransformation: state.currenAssetTransformation
        }),
        action => ({
            play: action.animateAssets,
            fadeIn: action.fadeAssets,
            setCameraConfig: action.setCameraConfig,
            setDrawingLines: action.setDrawingLines,
            setObjectsToTransform: action.setObjectsToTransform
        })
    );

    const copyText = (target: string) => {
        const textToCopy = document.getElementById(target) as HTMLDivElement;
        const range = document.createRange();

        try {
            range.selectNode(textToCopy);
            window.getSelection()?.removeAllRanges();
            window.getSelection()?.addRange(range);
            document.execCommand('copy');
            window.getSelection()?.removeAllRanges();
        } catch (err) {
            console.log('Error while copying to clipboard: ' + err);
        }
        //navigator.clipboard.writeText(document.getElementById('camera-view-config')!.textContent!);
    };

    return isEnabled ? (
        <FixedInfo>
            <div>
                <b>Current Camera-Config:</b>
            </div>
            <div
                id="camera-view-config"
                style={{
                    padding: '5px',
                    fontFamily: 'monospace',
                    backgroundColor: 'lightgray',
                    fontSize: '0.4rem',
                    wordBreak: 'break-all'
                }}
            >
                &#123;
                <br /> position: new Vector3({orbitCameraConfig?.position.x?.toFixed(2)},
                {orbitCameraConfig?.position.y?.toFixed(2)},{orbitCameraConfig?.position.z?.toFixed(2)}),
                <br />
                quaternion: new Quaternion({orbitCameraConfig?.quaternion.x?.toFixed(2)},
                {orbitCameraConfig?.quaternion.y?.toFixed(2)},{orbitCameraConfig?.quaternion.z?.toFixed(2)},
                {orbitCameraConfig?.quaternion.w?.toFixed(2)}),
                <br /> target: new Vector3(
                {orbitCameraConfig?.target?.x?.toFixed(2)},{orbitCameraConfig?.target?.y?.toFixed(2)},
                {orbitCameraConfig?.target?.z?.toFixed(2)})<br /> &#125;
            </div>
            <button onClick={() => copyText('camera-view-config')}>Copy!</button>
            <div>
                <b>Current Asset-Transformations:</b>
            </div>
            <div
                id="asset-view-config"
                style={{
                    padding: '5px',
                    fontFamily: 'monospace',
                    backgroundColor: 'lightgray',
                    fontSize: '0.4rem',
                    wordBreak: 'break-all'
                }}
            >
                &#123;
                <br /> name: "{currenAssetTransformation?.name}",
                <br /> position: new Vector3({currenAssetTransformation?.position.x?.toFixed(2)},
                {currenAssetTransformation?.position.y?.toFixed(2)},{currenAssetTransformation?.position.z?.toFixed(2)}
                ),
                <br />
                quaternion: new Quaternion({currenAssetTransformation?.quaternion.x?.toFixed(2)},
                {currenAssetTransformation?.quaternion.y?.toFixed(2)},
                {currenAssetTransformation?.quaternion.z?.toFixed(2)},
                {currenAssetTransformation?.quaternion.w?.toFixed(2)})<br /> &#125;
            </div>
            <button onClick={() => copyText('asset-view-config')}>Copy!</button>
            <div>
                <b>Animations:</b>{' '}
            </div>
            <div style={{ border: '1px dashed', padding: '10px' }}>
                {Object.entries(animations || {}).map(([key]) => (
                    <div key={key}>
                        <button onClick={() => play([{ name: key }])}>{key}</button>
                    </div>
                )) || 'Loading...'}
            </div>
            <div>
                <b>Fade Ins:</b>
            </div>
            <div style={{ border: '1px dashed', padding: '10px' }}>
                {[
                    ...boardConfig.opacityAdjustmentList,
                    ...boardConfig.assets.filter(a => a.id.startsWith('highlight')).map(i => i.id)
                ].map(id => (
                    <div key={id}>
                        <button onClick={() => fadeIn([id])}>{id}</button>
                    </div>
                ))}
            </div>
            <div>
                <b>Camera-Positions:</b>
            </div>
            <div style={{ border: '1px dashed', padding: '10px' }}>
                {Object.keys(boardConfig.cameraConfigs).map(id => (
                    <div key={id}>
                        <button onClick={() => setCameraConfig(boardConfig.cameraConfigs[id])}>{id}</button>
                    </div>
                )) || 'Loading...'}
            </div>
            <div>
                <b>Asset-Transformations:</b>
            </div>
            <div style={{ border: '1px dashed', padding: '10px' }}>
                {Object.keys(boardConfig.assetTransformations).map(assetName => (
                    <div key={assetName}>
                        <span>{assetName}:</span>
                        {Object.keys(boardConfig.assetTransformations[assetName]).map(id => (
                            <button
                                style={{ marginLeft: '20px', display: 'block' }}
                                onClick={() =>
                                    setObjectsToTransform([boardConfig.assetTransformations[assetName][id] as any])
                                }
                            >
                                {id}
                            </button>
                        ))}
                    </div>
                )) || 'Loading...'}
            </div>
            <div>
                <b>Lines:</b>
            </div>
            <div style={{ border: '1px dashed', padding: '10px' }}>
                {boardConfig.linesToDraw.map((line, id) => (
                    <div key={`line-${id}`}>
                        <button
                            onClick={() => {
                                if (
                                    linesToDraw.current.find(l => l.source === line.source && l.target === line.target)
                                ) {
                                    linesToDraw.current = linesToDraw.current.filter(
                                        l => l.source !== line.source && l.target !== line.target
                                    );
                                } else {
                                    linesToDraw.current.push(line);
                                }
                                setDrawingLines(linesToDraw.current);
                            }}
                        >
                            {line.source} &#8594 {line.target}
                        </button>
                    </div>
                )) || 'Loading...'}
            </div>
        </FixedInfo>
    ) : null;
};
